import { Button, Divider, Modal, ModalFooter, ModalHeader } from '@nextui-org/react'
import { FC, ReactNode } from 'react'
import { useMobileBackButton, useModalBodyClass } from '../../hooks/useMobileBackButton'

type SizeType =
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | 'xs'
  | '3xl'
  | '4xl'
  | '5xl'
  | 'full'
  | undefined
type BackDropType = 'transparent' | 'opaque' | 'blur' | undefined

type BaseModalProps = {
  isOpen: boolean
  onOpenChange: () => void
  size: SizeType
  children: ReactNode
  backdrop?: BackDropType
  isDismissable?: boolean
  'data-testid'?: string
  onBrowserBack?: () => void
  classNames?: {
    wrapper?: string
    backdrop?: string
    base?: string
  }
}

export const BaseModal: FC<BaseModalProps> = ({
  isOpen,
  onOpenChange,
  children,
  size,
  backdrop,
  isDismissable,
  'data-testid': dataTestId,
  onBrowserBack,
  classNames: customClassNames,
}) => {
  useMobileBackButton(
    isOpen,
    () => {
      onOpenChange()
    },
    onBrowserBack
      ? onBrowserBack
      : () => {
          window.history.back()
        },
  )

  useModalBodyClass(isOpen)

  return (
    <Modal
      className="select-tanks-modal"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable ?? false}
      size={size}
      backdrop={backdrop}
      hideCloseButton={true}
      isKeyboardDismissDisabled={false}
      scrollBehavior="inside"
      data-testid={dataTestId}
      classNames={{
        wrapper: customClassNames?.wrapper || 'z-[1400]',
        backdrop: customClassNames?.backdrop || 'z-[1300]',
        base: customClassNames?.base || 'max-h-[95vh] md:max-h-[90vh]',
      }}
      motionProps={{
        variants: {
          enter: { y: 0, opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } },
          exit: { y: 0, opacity: 0, transition: { duration: 0, ease: 'easeIn' } },
        },
      }}
    >
      {children}
    </Modal>
  )
}

type BaseModalHeaderProps = {
  title: string
  description: string | ReactNode
  'data-testid'?: string
}

export const BaseModalHeader: FC<BaseModalHeaderProps> = ({
  title,
  description,
  'data-testid': dataTestId,
}) => {
  return (
    <ModalHeader
      className="flex flex-col gap-6 pt-6 sm:pt-12 pl-6 sm:pl-10 pr-6 sm:pr-10 font-bold"
      style={{ fontSize: '2rem', lineHeight: '1.125' }}
      data-testid={dataTestId}
    >
      {title}
      <small className="" style={{ fontSize: '0.9rem', fontWeight: 'normal' }}>
        {description}
      </small>
    </ModalHeader>
  )
}

type BaseModalFooterProps = {
  back: string
  next?: string
  backOnPress?: () => void
  nextOnPress?: () => void
  nextClassName?: string
}

export const BaseModalFooter: FC<BaseModalFooterProps> = ({
  back,
  next,
  backOnPress,
  nextOnPress,
  nextClassName,
}) => {
  return (
    <ModalFooter className="pb-6 sm:pb-8 pl-6 sm:pl-10 pr-6 sm:pr-10" data-testid="modal-footer">
      <Button
        className="mr-3 font-bold w-[6rem] bg-tdpllightgrey text-tdpldarkgrey"
        style={{ marginLeft: 0 }}
        onPress={backOnPress}
        data-testid="modal-back-button"
        aria-label={`${back} button`}
      >
        {back}
      </Button>
      {next && (
        <Button
          className={'font-bold text-white w-[6rem] mr-0 bg-tdplplum ' + (nextClassName ?? '')}
          style={{ marginLeft: 0 }}
          onPress={nextOnPress}
          data-testid="modal-next-button"
          aria-label={`${next} button`}
        >
          {next}
        </Button>
      )}
    </ModalFooter>
  )
}

export const BaseDivider = () => {
  return <Divider className="my-4 bg-tdpllightgrey" style={{ margin: '1px' }} />
}
